import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import WorkspaceProvider from '../WorkspaceProvider';
import { AdminConfigProvider } from '../AdminConfigProvider';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { PageEnter } from '../ui/PageEnter';

/**
 * Admin shell. Uses the same Sidebar + Topbar as the regular dashboard —
 * the sidebar's "Admin" section shows up conditionally when
 * `useMe().systemRole === 'admin'`, so admins navigate seamlessly between
 * user and admin areas without a separate chrome.
 *
 * Adds a thin "admin banner" strip under the topbar so it's obvious you're
 * in the admin area with elevated permissions.
 */
export function AdminLayout() {
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  return (
    <WorkspaceProvider>
      {/* AdminConfigProvider also wraps DashboardLayout. Both shells need
          it because the sidebar's mockOnly nav gating and MockPlatformPage's
          route-level redirect both call useAdminConfig(). Without the
          wrapper here, visiting /admin/mock-platform sees the context
          default {mockEnabled: false} and bounces to /dashboard. */}
      <AdminConfigProvider>
        {/* Same scroll model as DashboardLayout: document locked to viewport
          height, `<main>` is the only scroll container. Topbar + admin banner
          sit above the scroll area and never overlap content. */}
        <div className="bg-page text-text-primary flex h-screen overflow-hidden">
          <Sidebar isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />

          <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
            <Topbar onMobileMenuClick={() => setMobileNavOpen(true)} />

            {/* Admin banner — inside the non-scrolling column, above <main> */}
            <div className="border-accent/20 bg-accent/10 mx-4 mt-2 flex flex-shrink-0 items-center justify-between rounded-xl border px-4 py-2 text-xs md:mx-8">
              <span className="text-accent font-bold tracking-widest uppercase">Admin mode</span>
              <Link
                to="/dashboard"
                className="text-accent flex items-center gap-1 font-semibold hover:underline"
              >
                <ArrowLeft className="h-3 w-3" />
                Back to dashboard
              </Link>
            </div>

            <main className="flex-1 overflow-y-auto px-4 pt-4 pb-16 md:px-8 md:pt-6 md:pb-20">
              {/* See DashboardLayout for the two-layer transition rationale:
                outer motion.div cross-fades routes, inner <PageEnter> does
                the Kora-style 20px slide-up reveal on mount. */}
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={location.pathname}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="mx-auto w-full max-w-7xl"
                >
                  <PageEnter>
                    <Outlet />
                  </PageEnter>
                </motion.div>
              </AnimatePresence>
            </main>
          </div>
        </div>
      </AdminConfigProvider>
    </WorkspaceProvider>
  );
}
